// ---------------------------------------------------------------------------
// Putting a cart aside.
//
// Every field is optional, deliberately. The commonest hold in a butcher shop
// is "that man there, one minute" - and a dialog that insisted on a name and a
// phone number before letting the cashier serve the next customer would simply
// stop being used.
//
// The one thing it says plainly is what holding does NOT do: nothing is sold,
// nothing is reserved. A cashier who believes holding a cart puts the meat
// aside will promise it to somebody.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { PauseCircle } from 'lucide-react';
import { Modal } from '../common/Modal';
import { useI18n } from '../../i18n/I18nProvider';
import { formatUsd } from '../../utils/format';
import type { Customer } from '../../types';

export interface HoldDetails {
  customerId: string | null;
  label: string;
  customerName: string;
  phone: string;
  orderNote: string;
}

export function HoldOrderDialog({
  open,
  onClose,
  onConfirm,
  submitting,
  itemCount,
  totalUsd,
  customer,
  orderNote,
  errorMessage,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (details: HoldDetails) => void;
  submitting: boolean;
  itemCount: number;
  totalUsd: string;
  /// Already chosen on the POS, if any. Carried through so the cashier does
  /// not have to pick the same regular twice.
  customer: Customer | null;
  orderNote: string;
  errorMessage: string | null;
}) {
  const { t } = useI18n();

  const [label, setLabel] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState(orderNote);

  const submit = () => {
    onConfirm({
      customerId: customer?.id ?? null,
      label: label.trim(),
      customerName: customerName.trim(),
      phone: phone.trim(),
      orderNote: note.trim(),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t.held.holdTitle}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={submitting}>
            {t.common.cancel}
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={submitting}>
            {submitting ? t.held.holding : t.held.confirmHold}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-control bg-surface-soft p-3">
          <PauseCircle className="mt-0.5 h-5 w-5 shrink-0 text-butcher-green" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-sm text-ink">{t.held.holdBody}</p>
            <p className="numeric mt-1 text-sm font-semibold text-ink">
              {t.held.items.replace('{count}', String(itemCount))} · {formatUsd(totalUsd)}
            </p>
          </div>
        </div>

        {/* A regular already picked on the till carries through. */}
        {customer ? (
          <p className="text-sm text-ink-muted">
            {t.held.chooseCustomer}: <span className="font-medium text-ink">{customer.name}</span>
          </p>
        ) : null}

        <div>
          <label htmlFor="hold-label" className="label text-xs">
            {t.held.label}
          </label>
          <input
            id="hold-label"
            type="text"
            className="input"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder={t.held.labelHint}
          />
          <p className="mt-1 text-xs text-ink-muted">{t.held.labelHint}</p>
        </div>

        {!customer ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="hold-name" className="label text-xs">
                {t.held.customerName}
              </label>
              <input
                id="hold-name"
                type="text"
                className="input"
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
              />
            </div>

            <div>
              <label htmlFor="hold-phone" className="label text-xs">
                {t.held.phone}
              </label>
              <input
                id="hold-phone"
                type="tel"
                className="input"
                dir="ltr"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            </div>
          </div>
        ) : null}

        <div>
          <label htmlFor="hold-note" className="label text-xs">
            {t.held.note}
          </label>
          <input
            id="hold-note"
            type="text"
            className="input"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        {errorMessage ? (
          <p className="text-sm text-danger" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
